/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { Button } from "@/components/shadcn/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/shadcn/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/shadcn/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@/components/shadcn/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { cn } from "@/lib/utils";
import type { ChatStatus, FileUIPart } from "ai";
import {
  ArrowUp,
  CornerDownLeftIcon,
  ImageIcon,
  LanguagesIcon,
  Loader2Icon,
  MicIcon,
  PaperclipIcon,
  PlusIcon,
  SquareIcon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useLocale, useTranslations } from "next-intl";
import {
  type ChangeEvent,
  type ChangeEventHandler,
  Children,
  type ClipboardEventHandler,
  type ComponentProps,
  createContext,
  type FormEvent,
  type FormEventHandler,
  Fragment,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StorageService } from "@/services/Storage/storage";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import { getSpotFileUrl } from "@/utils/generic";
import { ChatWidgetContext } from "../NewAndIncredibleChatWidget/NewAndIncredibleChatWidget";

const MAX_FILES = 10;
const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30MB

// ============================================================================
// Provider Context & Types
// ============================================================================

export type AttachmentsContext = {
  files: (FileUIPart & { id: string; loading?: boolean })[];
  add: (files: File[] | FileList) => void;
  remove: (id: string) => void;
  clear: () => void;
  openFileDialog: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
};

export type TextInputContext = {
  value: string;
  setInput: (v: string) => void;
  clear: () => void;
};

export type PromptInputControllerProps = {
  textInput: TextInputContext;
  attachments: AttachmentsContext;
  /** INTERNAL: Allows PromptInput to register its file textInput + "open" callback */
  __registerFileInput: (
    ref: RefObject<HTMLInputElement | null>,
    open: () => void
  ) => void;
};

const PromptInputController = createContext<PromptInputControllerProps | null>(
  null
);
const ProviderAttachmentsContext = createContext<AttachmentsContext | null>(
  null
);

export const usePromptInputController = () => {
  const ctx = useContext(PromptInputController);
  if (!ctx) {
    throw new Error(
      "Wrap your component inside <PromptInputProvider> to use usePromptInputController()."
    );
  }
  return ctx;
};

// Optional variants (do NOT throw). Useful for dual-mode components.
const useOptionalPromptInputController = () =>
  useContext(PromptInputController);

export const useProviderAttachments = () => {
  const ctx = useContext(ProviderAttachmentsContext);
  if (!ctx) {
    throw new Error(
      "Wrap your component inside <PromptInputProvider> to use useProviderAttachments()."
    );
  }
  return ctx;
};

const useOptionalProviderAttachments = () =>
  useContext(ProviderAttachmentsContext);

export type PromptInputProviderProps = PropsWithChildren<{
  initialInput?: string;
}>;

/**
 * Optional global provider that lifts PromptInput state outside of PromptInput.
 * If you don't use it, PromptInput stays fully self-managed.
 */
export function PromptInputProvider({
  initialInput: initialTextInput = "",
  children,
}: PromptInputProviderProps) {
  const { spotGroup } = useSpotUser();

  // ----- textInput state
  const [textInput, setTextInput] = useState(initialTextInput);
  const clearInput = useCallback(() => setTextInput(""), []);

  // ----- attachments state (global when wrapped)
  const [attachements, setAttachements] = useState<
    (FileUIPart & { id: string; loading?: boolean })[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const openRef = useRef<() => void>(() => {});

  const { isPublic, publicAuth } = useContext(ChatWidgetContext);

  const add = useCallback(
    async (files: File[] | FileList) => {
      const incoming = Array.from(files);
      if (incoming.length === 0) {
        return;
      }

      if (attachements.length + incoming.length > MAX_FILES) {
        SpotToast.error(
          `Cannot add ${incoming.length} file(s). Maximum ${MAX_FILES} files allowed (${attachements.length} already selected).`
        );
        return;
      }

      const oversizedFile = incoming.find((f) => f.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFile) {
        SpotToast.error(`File "${oversizedFile.name}" exceeds the 30MB limit.`);
        return;
      }

      const loadingAttachments = incoming.map((file) => ({
        id: nanoid(),
        type: "file" as const,
        url: URL.createObjectURL(file),
        mediaType: file.type,
        filename: file.name,
        loading: true,
      }));

      setAttachements((prev) => prev.concat(loadingAttachments));

      const uploadPromises = incoming.map(async (file, index) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
          if (isPublic && !publicAuth)
            throw new Error("Missing widget credentials");
          const uploadedFile = publicAuth
            ? await StorageService.uploadFilePublic(formData, publicAuth)
            : await StorageService.uploadFile(
                spotGroup?.id,
                formData,
                isPublic
              );

          return {
            id: loadingAttachments[index].id,
            uploadedFile,
            success: true as const,
            file,
          };
        } catch (error) {
          console.error("Failed to upload file:", error);
          SpotToast.error(`Failed to upload ${file.name}`);
          return {
            id: loadingAttachments[index].id,
            success: false as const,
            file,
          };
        }
      });

      const results = await Promise.all(uploadPromises);

      setAttachements((prev) =>
        prev.map((attachment) => {
          const result = results.find((r) => r.id === attachment.id);
          if (!result) return attachment;

          if (!result.success) {
            return attachment;
          }

          if (attachment.url.startsWith("blob:")) {
            URL.revokeObjectURL(attachment.url);
          }

          return {
            ...attachment,
            url: `spot://${result.uploadedFile.id}`,
            loading: false,
          };
        })
      );

      setAttachements((prev) =>
        prev.filter((attachment) => {
          const result = results.find((r) => r.id === attachment.id);
          return !result || result.success;
        })
      );
    },
    [spotGroup?.id, attachements.length, isPublic, publicAuth]
  );

  const remove = useCallback((id: string) => {
    setAttachements((prev) => {
      const found = prev.find((f) => f.id === id);
      if (found?.url && found.url.startsWith("blob:")) {
        URL.revokeObjectURL(found.url);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setAttachements((prev) => {
      for (const f of prev) {
        if (f.url && f.url.startsWith("blob:")) {
          URL.revokeObjectURL(f.url);
        }
      }
      return [];
    });
  }, []);

  const openFileDialog = useCallback(() => {
    openRef.current?.();
  }, []);

  const attachments = useMemo<AttachmentsContext>(
    () => ({
      files: attachements,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef,
    }),
    [attachements, add, remove, clear, openFileDialog]
  );

  const __registerFileInput = useCallback(
    (ref: RefObject<HTMLInputElement | null>, open: () => void) => {
      fileInputRef.current = ref.current;
      openRef.current = open;
    },
    []
  );

  const controller = useMemo<PromptInputControllerProps>(
    () => ({
      textInput: {
        value: textInput,
        setInput: setTextInput,
        clear: clearInput,
      },
      attachments,
      __registerFileInput,
    }),
    [textInput, clearInput, attachments, __registerFileInput]
  );

  return (
    <PromptInputController.Provider value={controller}>
      <ProviderAttachmentsContext.Provider value={attachments}>
        {children}
      </ProviderAttachmentsContext.Provider>
    </PromptInputController.Provider>
  );
}

// ============================================================================
// Component Context & Hooks
// ============================================================================

const LocalAttachmentsContext = createContext<AttachmentsContext | null>(null);

export type AttachmentPolicy = {
  /** False when the surface takes no attachments at all (e.g. an agent with
   * the Files tool disabled). Paste and drop must both honour it. */
  canAttach: boolean;
  matchesAccept: (file: File) => boolean;
  /** Shown when canAttach is false — the reason differs per surface. */
  rejectionMessage: string;
};

const DEFAULT_REJECTION_MESSAGE = "This assistant can't receive files.";

const AttachmentPolicyContext = createContext<AttachmentPolicy>({
  canAttach: true,
  matchesAccept: () => true,
  rejectionMessage: DEFAULT_REJECTION_MESSAGE,
});

export const useAttachmentPolicy = () => useContext(AttachmentPolicyContext);

/** Files the surface will take, warning about whatever it drops. */
const attachableFiles = (files: File[], policy: AttachmentPolicy): File[] => {
  if (files.length === 0) return [];
  if (!policy.canAttach) {
    SpotToast.warn(policy.rejectionMessage);
    return [];
  }
  const accepted = files.filter((file) => policy.matchesAccept(file));
  if (accepted.length === 0) {
    SpotToast.warn(
      files.length === 1
        ? `"${files[0].name}" is not a supported file type.`
        : "None of those files are a supported type."
    );
  }
  return accepted;
};

export const usePromptInputAttachments = () => {
  // Dual-mode: prefer provider if present, otherwise use local
  const provider = useOptionalProviderAttachments();
  const local = useContext(LocalAttachmentsContext);
  const context = provider ?? local;
  if (!context) {
    throw new Error(
      "usePromptInputAttachments must be used within a PromptInput or PromptInputProvider"
    );
  }
  return context;
};

export type PromptInputAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart & { id: string; loading?: boolean };
  className?: string;
  isPublic?: boolean;
};

export function PromptInputAttachment({
  data,
  className,
  isPublic,
  ...props
}: PromptInputAttachmentProps) {
  const attachments = usePromptInputAttachments();

  const filename = data.filename || "";

  const mediaType =
    data.mediaType?.startsWith("image/") && data.url ? "image" : "file";
  const isImage = mediaType === "image";

  const attachmentLabel = filename || (isImage ? "Image" : "Attachment");

  const imageUrl = data.url?.startsWith("spot://")
    ? getSpotFileUrl(data.url, false, isPublic)
    : data.url;

  return (
    <PromptInputHoverCard>
      <HoverCardTrigger asChild>
        <div
          className={cn(
            "group relative flex h-6 cursor-default select-none items-center gap-1.5 rounded-md border border-border px-1.5 font-medium text-sm transition-all bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
            data.loading && "opacity-60",
            "max-w-[160px]",
            className
          )}
          key={data.id}
          {...props}
        >
          <div className="relative size-5 shrink-0">
            <div className="absolute inset-0 flex size-5 items-center justify-center overflow-hidden rounded transition-opacity group-hover:opacity-0">
              {data.loading ? (
                <Loader2Icon className="size-3 animate-spin text-muted-foreground" />
              ) : isImage ? (
                <img
                  alt={filename || "attachment"}
                  className="size-4 object-cover"
                  height={20}
                  src={imageUrl}
                  width={20}
                />
              ) : (
                <div className="flex size-5 items-center justify-center text-muted-foreground">
                  <PaperclipIcon className="size-3" />
                </div>
              )}
            </div>
            {!data.loading && (
              <Button
                aria-label="Remove attachment"
                className="absolute inset-0 size-5 cursor-pointer rounded p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 [&>svg]:size-2.5"
                onClick={(e) => {
                  e.stopPropagation();
                  attachments.remove(data.id);
                }}
                type="button"
                variant="ghost"
              >
                <XIcon />
                <span className="sr-only">Remove</span>
              </Button>
            )}
          </div>

          <span className="flex-1 truncate text-xs">{attachmentLabel}</span>
        </div>
      </HoverCardTrigger>
      <PromptInputHoverCardContent className="w-auto p-2">
        <div className="w-auto space-y-3">
          {isImage && !data.loading && (
            <div className="flex max-h-96 w-96 items-center justify-center overflow-hidden rounded-md border">
              <img
                alt={filename || "attachment preview"}
                className="max-h-full max-w-full object-contain"
                height={384}
                src={imageUrl}
                width={448}
              />
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <div className="min-w-0 flex-1 space-y-1 px-0.5">
              <h4 className="truncate font-semibold text-sm leading-none">
                {filename || (isImage ? "Image" : "Attachment")}
              </h4>
              {data.mediaType && (
                <p className="truncate font-mono text-muted-foreground text-xs">
                  {data.mediaType}
                </p>
              )}
            </div>
          </div>
        </div>
      </PromptInputHoverCardContent>
    </PromptInputHoverCard>
  );
}

export type PromptInputAttachmentsProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  children: (
    attachment: FileUIPart & { id: string; loading?: boolean }
  ) => ReactNode;
};

export function PromptInputAttachments({
  children,
  className,
  ...props
}: PromptInputAttachmentsProps) {
  const attachments = usePromptInputAttachments();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    if ((e.target as HTMLElement).closest("button")) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    startXRef.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  if (!attachments.files.length) {
    return null;
  }

  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        "flex items-center gap-2 py-1.5 overflow-x-auto -mx-3 px-3 [&_button]:cursor-pointer",
        isDragging ? "cursor-grabbing [&_*]:cursor-grabbing" : "cursor-grab",
        className
      )}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      {...props}
    >
      {attachments.files.map((file) => (
        <Fragment key={file.id}>{children(file)}</Fragment>
      ))}
    </div>
  );
}

export type PromptInputActionAddAttachmentsProps = ComponentProps<
  typeof DropdownMenuItem
> & {
  label?: string;
};

export const PromptInputActionAddAttachments = ({
  label = "Add files",
  ...props
}: PromptInputActionAddAttachmentsProps) => {
  const attachments = usePromptInputAttachments();

  return (
    <DropdownMenuItem
      {...props}
      onSelect={(e) => {
        e.preventDefault();
        attachments.openFileDialog();
      }}
    >
      <UploadIcon className="mr-2 size-4" /> {label}
    </DropdownMenuItem>
  );
};

export type PromptInputMessage = {
  text: string;
  files: FileUIPart[];
};

export type PromptInputProps = Omit<
  HTMLAttributes<HTMLFormElement>,
  "onSubmit" | "onError"
> & {
  accept?: string; // e.g., "image/*" or leave undefined for any
  multiple?: boolean;
  // When true, accepts drops anywhere on document. Default false (opt-in).
  globalDrop?: boolean;
  // When true, dropped/pasted files are rejected (still preventDefault to block browser navigation).
  disableDrop?: boolean;
  // Why attachments are refused, shown to the user when disableDrop is set.
  rejectionMessage?: string;
  // Render a hidden input with given name and keep it in sync for native form posts. Default false.
  syncHiddenInput?: boolean;
  // Minimal constraints
  maxFiles?: number;
  maxFileSize?: number; // bytes
  onError?: (err: {
    code: "max_files" | "max_file_size" | "accept";
    message: string;
  }) => void;
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => void | Promise<void>;
};

export const PromptInput = ({
  className,
  accept,
  multiple,
  globalDrop,
  disableDrop,
  rejectionMessage,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  onError,
  onSubmit,
  children,
  ...props
}: PromptInputProps) => {
  const { spotGroup } = useSpotUser();

  // Try to use a provider controller if present
  const controller = useOptionalPromptInputController();
  const usingProvider = !!controller;

  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Find nearest form to scope drag & drop
  useEffect(() => {
    const root = anchorRef.current?.closest("form");
    if (root instanceof HTMLFormElement) {
      formRef.current = root;
    }
  }, []);

  // ----- Local attachments (only used when no provider)
  const [items, setItems] = useState<
    (FileUIPart & { id: string; loading?: boolean })[]
  >([]);
  const files = usingProvider ? controller.attachments.files : items;

  const openFileDialogLocal = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const matchesAccept = useCallback(
    (f: File) => {
      if (!accept || accept.trim() === "") {
        return true;
      }
      const acceptTypes = accept.split(",").map((t) => t.trim());
      return acceptTypes.some((acceptType) => {
        if (acceptType.endsWith("/*")) {
          const prefix = acceptType.slice(0, -2);
          return f.type.startsWith(prefix + "/");
        }
        return f.type === acceptType;
      });
    },
    [accept]
  );

  const { isPublic, publicAuth } = useContext(ChatWidgetContext);

  const addLocal = useCallback(
    async (fileList: File[] | FileList) => {
      const incoming = Array.from(fileList);
      const accepted = incoming.filter((f) => matchesAccept(f));
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: "accept",
          message: "No files match the accepted types.",
        });
        return;
      }

      if (items.length + accepted.length > MAX_FILES) {
        onError?.({
          code: "max_files",
          message: `Cannot add ${accepted.length} file(s). Maximum ${MAX_FILES} files allowed.`,
        });
        SpotToast.error(
          `Cannot add ${accepted.length} file(s). Maximum ${MAX_FILES} files allowed (${items.length} already selected).`
        );
        return;
      }

      const oversizedFile = accepted.find((f) => f.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFile) {
        onError?.({
          code: "max_file_size",
          message: `File "${oversizedFile.name}" exceeds the 30MB limit.`,
        });
        SpotToast.error(`File "${oversizedFile.name}" exceeds the 30MB limit.`);
        return;
      }

      const loadingAttachments = accepted.map((file) => ({
        id: nanoid(),
        type: "file" as const,
        url: URL.createObjectURL(file),
        mediaType: file.type,
        filename: file.name,
        loading: true,
      }));

      setItems((prev) => prev.concat(loadingAttachments));

      const uploadPromises = accepted.map(async (file, index) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
          if (isPublic && !publicAuth)
            throw new Error("Missing widget credentials");
          const uploadedFile = publicAuth
            ? await StorageService.uploadFilePublic(formData, publicAuth)
            : await StorageService.uploadFile(
                spotGroup?.id,
                formData,
                isPublic
              );

          return {
            id: loadingAttachments[index].id,
            uploadedFile,
            success: true as const,
            file,
          };
        } catch (error) {
          console.error("Failed to upload file:", error);
          SpotToast.error(`Failed to upload ${file.name}`);
          return {
            id: loadingAttachments[index].id,
            success: false as const,
            file,
          };
        }
      });

      const results = await Promise.all(uploadPromises);

      setItems((prev) =>
        prev.map((attachment) => {
          const result = results.find((r) => r.id === attachment.id);
          if (!result) return attachment;

          if (!result.success) {
            return attachment;
          }

          if (attachment.url.startsWith("blob:")) {
            URL.revokeObjectURL(attachment.url);
          }

          return {
            ...attachment,
            url: `spot://${result.uploadedFile.id}`,
            loading: false,
          };
        })
      );

      setItems((prev) =>
        prev.filter((attachment) => {
          const result = results.find((r) => r.id === attachment.id);
          return !result || result.success;
        })
      );
    },
    [matchesAccept, onError, spotGroup?.id, items.length, isPublic, publicAuth]
  );

  const add = usingProvider
    ? (files: File[] | FileList) => controller.attachments.add(files)
    : addLocal;

  const policy = useMemo<AttachmentPolicy>(
    () => ({
      canAttach: !disableDrop,
      matchesAccept,
      rejectionMessage: rejectionMessage ?? DEFAULT_REJECTION_MESSAGE,
    }),
    [disableDrop, matchesAccept, rejectionMessage]
  );

  // The provider's ``add`` knows nothing about ``accept`` or ``disableDrop``,
  // so every entry point (drop, paste) funnels through the policy instead.
  const addAccepted = useCallback(
    (incoming: File[] | FileList) => {
      const accepted = attachableFiles(Array.from(incoming), policy);
      if (accepted.length > 0) {
        add(accepted);
      }
    },
    [add, policy]
  );

  const remove = usingProvider
    ? (id: string) => controller.attachments.remove(id)
    : (id: string) =>
        setItems((prev) => {
          const found = prev.find((file) => file.id === id);
          if (found?.url && found.url.startsWith("blob:")) {
            URL.revokeObjectURL(found.url);
          }
          return prev.filter((file) => file.id !== id);
        });

  const clear = usingProvider
    ? () => controller.attachments.clear()
    : () =>
        setItems((prev) => {
          for (const file of prev) {
            if (file.url && file.url.startsWith("blob:")) {
              URL.revokeObjectURL(file.url);
            }
          }
          return [];
        });

  const openFileDialog = usingProvider
    ? () => controller.attachments.openFileDialog()
    : openFileDialogLocal;

  // Let provider know about our hidden file input so external menus can call openFileDialog()
  useEffect(() => {
    if (!usingProvider) return;
    controller.__registerFileInput(inputRef, () => inputRef.current?.click());
  }, [usingProvider, controller]);

  // Note: File input cannot be programmatically set for security reasons
  // The syncHiddenInput prop is no longer functional
  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = "";
    }
  }, [files, syncHiddenInput]);

  // Attach drop handlers on nearest form and document (opt-in)
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        addAccepted(e.dataTransfer.files);
      }
    };
    form.addEventListener("dragover", onDragOver);
    form.addEventListener("drop", onDrop);
    return () => {
      form.removeEventListener("dragover", onDragOver);
      form.removeEventListener("drop", onDrop);
    };
  }, [addAccepted, disableDrop]);

  useEffect(() => {
    if (!globalDrop) return;

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        addAccepted(e.dataTransfer.files);
      }
    };
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, [addAccepted, globalDrop, disableDrop]);

  useEffect(
    () => () => {
      if (!usingProvider) {
        for (const f of files) {
          if (f.url && f.url.startsWith("blob:")) {
            URL.revokeObjectURL(f.url);
          }
        }
      }
    },
    [usingProvider, files]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.currentTarget.files) {
      add(event.currentTarget.files);
    }
  };

  const convertBlobUrlToDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const ctx = useMemo<AttachmentsContext>(
    () => ({
      files: files.map((item) => ({ ...item, id: item.id })),
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef: inputRef,
    }),
    [files, add, remove, clear, openFileDialog]
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const text = usingProvider
      ? controller.textInput.value
      : (() => {
          const formData = new FormData(form);
          return (formData.get("message") as string) || "";
        })();

    const hasLoadingFiles = files.some((f) => f.loading);
    if (hasLoadingFiles) {
      SpotToast.error("Please wait for files to finish uploading");
      return;
    }

    // Reset form immediately after capturing text to avoid race condition
    // where user input during async blob conversion would be lost
    if (!usingProvider) {
      form.reset();
    }

    // Convert blob URLs to data URLs asynchronously, but keep spot:// URIs as-is
    Promise.all(
      files
        .filter((f) => !f.loading)
        .map(async ({ id, loading, ...item }) => {
          if (item.url && item.url.startsWith("blob:")) {
            return {
              ...item,
              url: await convertBlobUrlToDataUrl(item.url),
            };
          }
          return item;
        })
    ).then((convertedFiles: FileUIPart[]) => {
      try {
        const result = onSubmit({ text, files: convertedFiles }, event);

        // Handle both sync and async onSubmit
        if (result instanceof Promise) {
          result
            .then(() => {
              clear();
              if (usingProvider) {
                controller.textInput.clear();
              }
            })
            .catch(() => {
              // Don't clear on error - user may want to retry
            });
        } else {
          // Sync function completed without throwing, clear attachments
          clear();
          if (usingProvider) {
            controller.textInput.clear();
          }
        }
      } catch (error) {
        // Don't clear on error - user may want to retry
      }
    });
  };

  // Render with or without local provider
  const inner = (
    <>
      <span aria-hidden="true" className="hidden" ref={anchorRef} />
      <input
        accept={accept}
        aria-label="Upload files"
        className="hidden"
        multiple={multiple}
        onChange={handleChange}
        ref={inputRef}
        title="Upload files"
        type="file"
      />
      <form
        className={cn("w-full", className)}
        onSubmit={handleSubmit}
        {...props}
      >
        <InputGroup className="overflow-hidden">{children}</InputGroup>
      </form>
    </>
  );

  return (
    <AttachmentPolicyContext.Provider value={policy}>
      {usingProvider ? (
        inner
      ) : (
        <LocalAttachmentsContext.Provider value={ctx}>
          {inner}
        </LocalAttachmentsContext.Provider>
      )}
    </AttachmentPolicyContext.Provider>
  );
};

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputBody = ({
  className,
  ...props
}: PromptInputBodyProps) => (
  <div className={cn("contents", className)} {...props} />
);

export type PromptInputTextareaProps = ComponentProps<
  typeof InputGroupTextarea
>;

export const PromptInputTextarea = ({
  onChange,
  className,
  placeholder = "Ask me anything...",
  ...props
}: PromptInputTextareaProps) => {
  const controller = useOptionalPromptInputController();
  const attachments = usePromptInputAttachments();
  const policy = useAttachmentPolicy();
  const [isComposing, setIsComposing] = useState(false);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter") {
      if (isComposing || e.nativeEvent.isComposing) {
        return;
      }
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();

      // Check if the submit button is disabled before submitting
      const form = e.currentTarget.form;
      const submitButton = form?.querySelector(
        'button[type="submit"]'
      ) as HTMLButtonElement | null;
      if (submitButton?.disabled) {
        return;
      }

      form?.requestSubmit();
    }

    // Remove last attachment when Backspace is pressed and textarea is empty
    if (
      e.key === "Backspace" &&
      e.currentTarget.value === "" &&
      attachments.files.length > 0
    ) {
      e.preventDefault();
      const lastAttachment = attachments.files.at(-1);
      if (lastAttachment) {
        attachments.remove(lastAttachment.id);
      }
    }
  };

  const handlePaste: ClipboardEventHandler<HTMLTextAreaElement> = (event) => {
    const items = event.clipboardData?.items;

    if (!items) {
      return;
    }

    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    // Paste is the one entry point that bypasses the attach button, so it has
    // to enforce the same policy — an agent with the Files tool off used to
    // accept a pasted image and then break the whole conversation.
    const accepted = attachableFiles(files, policy);
    if (accepted.length === 0) {
      return;
    }

    event.preventDefault();
    attachments.add(accepted);
  };

  const controlledProps = controller
    ? {
        value: controller.textInput.value,
        onChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
          controller.textInput.setInput(e.currentTarget.value);
          onChange?.(e);
        },
      }
    : {
        onChange,
      };

  return (
    <InputGroupTextarea
      className={cn("field-sizing-content max-h-48 min-h-16", className)}
      name="message"
      onCompositionEnd={() => setIsComposing(false)}
      onCompositionStart={() => setIsComposing(true)}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder}
      {...props}
      {...controlledProps}
    />
  );
};

export type PromptInputHeaderProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  "align"
>;

export const PromptInputHeader = ({
  className,
  ...props
}: PromptInputHeaderProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn("order-first flex-wrap gap-1", className)}
    {...props}
  />
);

export type PromptInputFooterProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  "align"
>;

export const PromptInputFooter = ({
  className,
  ...props
}: PromptInputFooterProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn("justify-between gap-1", className)}
    {...props}
  />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div className={cn("flex items-center gap-1", className)} {...props} />
);

export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>;

export const PromptInputButton = ({
  variant = "ghost",
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const newSize =
    size ?? (Children.count(props.children) > 1 ? "sm" : "icon-sm");

  return (
    <InputGroupButton
      className={cn(className)}
      size={newSize}
      type="button"
      variant={variant}
      {...props}
    />
  );
};

export type PromptInputActionMenuProps = ComponentProps<typeof DropdownMenu>;
export const PromptInputActionMenu = (props: PromptInputActionMenuProps) => (
  <DropdownMenu {...props} />
);

export type PromptInputActionMenuTriggerProps = PromptInputButtonProps;

export const PromptInputActionMenuTrigger = ({
  className,
  children,
  ...props
}: PromptInputActionMenuTriggerProps) => (
  <DropdownMenuTrigger asChild>
    <PromptInputButton className={className} {...props}>
      {children ?? <PlusIcon className="size-4" />}
    </PromptInputButton>
  </DropdownMenuTrigger>
);

export type PromptInputActionMenuContentProps = ComponentProps<
  typeof DropdownMenuContent
>;
export const PromptInputActionMenuContent = ({
  className,
  ...props
}: PromptInputActionMenuContentProps) => (
  <DropdownMenuContent align="start" className={cn(className)} {...props} />
);

export type PromptInputActionMenuItemProps = ComponentProps<
  typeof DropdownMenuItem
>;
export const PromptInputActionMenuItem = ({
  className,
  ...props
}: PromptInputActionMenuItemProps) => (
  <DropdownMenuItem className={cn(className)} {...props} />
);

// Note: Actions that perform side-effects (like opening a file dialog)
// are provided in opt-in modules (e.g., prompt-input-attachments).

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
  status?: ChatStatus;
};

export const PromptInputSubmit = ({
  className,
  variant = "default",
  size = "icon-sm",
  status,
  children,
  ...props
}: PromptInputSubmitProps) => {
  const attachments = usePromptInputAttachments();
  const hasLoadingFiles = attachments.files.some((f) => f.loading);

  let Icon = <ArrowUp className="size-4" />;

  if (status === "submitted" || hasLoadingFiles || status === "streaming") {
    Icon = <Loader2Icon className="size-4 animate-spin" />;
  } else if (status === "error") {
    Icon = <XIcon className="size-4" />;
  }

  return (
    <InputGroupButton
      aria-label="Submit"
      className={cn(className)}
      disabled={hasLoadingFiles || props.disabled}
      size={size}
      type="submit"
      variant={variant}
      {...props}
    >
      {children ?? Icon}
    </InputGroupButton>
  );
};

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any)
    | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any)
    | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

type SpeechRecognitionResultList = {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
};

type SpeechRecognitionResult = {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
};

type SpeechRecognitionAlternative = {
  transcript: string;
  confidence: number;
};

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

declare global {
  interface Window {
    SpeechRecognition: {
      new (): SpeechRecognition;
    };
    webkitSpeechRecognition: {
      new (): SpeechRecognition;
    };
  }
}

type WaveformVisualizerProps = {
  isActive: boolean;
  audioStream: MediaStream | null;
};

const WAVEFORM_WIDTH = 32;
const WAVEFORM_HEIGHT = 16;

const WaveformVisualizer = ({
  isActive,
  audioStream,
}: WaveformVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const analyserRef = useRef<AnalyserNode | undefined>(undefined);

  useEffect(() => {
    if (!isActive || !audioStream) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    canvas.width = WAVEFORM_WIDTH * dpr;
    canvas.height = WAVEFORM_HEIGHT * dpr;
    ctx.scale(dpr, dpr);

    const audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioStream);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const barCount = 5;
    const frequencyRanges = [
      [2, 8],
      [8, 16],
      [16, 35],
    ];
    const sizeMultipliers = [0.5, 0.8, 1.0];

    const draw = () => {
      if (!canvasRef.current || !analyserRef.current) {
        return;
      }

      const drawCtx = canvasRef.current.getContext("2d");
      if (!drawCtx) {
        return;
      }

      analyserRef.current.getByteFrequencyData(dataArray);

      drawCtx.clearRect(0, 0, WAVEFORM_WIDTH, WAVEFORM_HEIGHT);

      const padding = 4;
      const availableWidth = WAVEFORM_WIDTH - padding * 2;
      const totalSpacing = 8;
      const spacing = totalSpacing / (barCount - 1);
      const totalBarWidth = availableWidth - totalSpacing;
      const barWidth = totalBarWidth / barCount;

      const heights: number[] = [];

      for (let i = 0; i < 3; i++) {
        const [start, end] = frequencyRanges[i] || [0, 10];
        let sum = 0;
        let count = 0;

        for (let j = start; j < end && j < dataArray.length; j++) {
          sum += dataArray[j] || 0;
          count++;
        }

        const avgValue = count > 0 ? sum / count : 0;
        const normalizedValue = avgValue / 255;
        const heightMultiplier = 1.5 + normalizedValue * 0.8;
        const barSizeMultiplier = sizeMultipliers[i] || 1.0;
        const barHeight =
          normalizedValue *
          WAVEFORM_HEIGHT *
          heightMultiplier *
          barSizeMultiplier;
        const minHeight = 3 * barSizeMultiplier;
        const finalHeight = Math.max(minHeight, barHeight);

        heights.push(finalHeight);
      }

      const symmetricalHeights = [
        heights[0],
        heights[1],
        heights[2],
        heights[1],
        heights[0],
      ];

      drawCtx.fillStyle = "#71717A";

      for (let i = 0; i < barCount; i++) {
        const finalHeight = symmetricalHeights[i] || 3;
        const x = padding + i * (barWidth + spacing);
        const y = (WAVEFORM_HEIGHT - finalHeight) / 2;
        const radius = Math.min(barWidth / 2, 1.5);

        if (drawCtx.roundRect) {
          drawCtx.beginPath();
          drawCtx.roundRect(x, y, barWidth, finalHeight, radius);
          drawCtx.fill();
        } else {
          drawCtx.fillRect(x, y, barWidth, finalHeight);
        }
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current?.state !== "closed") {
        audioContextRef.current?.close();
      }
    };
  }, [isActive, audioStream]);

  if (!isActive) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ width: WAVEFORM_WIDTH, height: WAVEFORM_HEIGHT }}
      className="absolute inset-0 m-auto"
    />
  );
};

export type PromptInputSpeechButtonProps = ComponentProps<
  typeof PromptInputButton
> & {
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  onTranscriptionChange?: (text: string) => void;
  defaultLanguage?: string;
  transcribe?: (audio: Blob, language: string) => Promise<string>;
};

const appendSpoken = (textarea: HTMLTextAreaElement, spoken: string) => {
  const currentValue = textarea.value;
  const newValue = currentValue + (currentValue ? " " : "") + spoken;
  textarea.value = newValue;
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  return newValue;
};

const SUPPORTED_LANGUAGES = [
  { code: "en-US", label: "English (US)" },
  { code: "en-GB", label: "English (UK)" },
  { code: "es-ES", label: "Spanish" },
  { code: "fr-FR", label: "French" },
  { code: "de-DE", label: "German" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "pt-PT", label: "Portuguese (Portugal)" },
];

export const PromptInputSpeechButton = ({
  className,
  textareaRef,
  onTranscriptionChange,
  defaultLanguage = "en-US",
  transcribe,
  ...props
}: PromptInputSpeechButtonProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const startingRef = useRef(false);
  const unmountedRef = useRef(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(
    null
  );
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [language, setLanguage] = useState(defaultLanguage);
  const [isHovered, setIsHovered] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const t = useTranslations("widget.composer");
  const locale = useLocale();
  const languageName = useMemo(
    () =>
      new Intl.DisplayNames([locale], { type: "language" }).of(language) ??
      language,
    [locale, language]
  );

  const isSpeechRecognitionSupported =
    typeof window !== "undefined" &&
    (window.SpeechRecognition !== undefined ||
      window.webkitSpeechRecognition !== undefined) &&
    !(navigator as any)?.brave;

  useEffect(() => {
    if (isSpeechRecognitionSupported && !transcribe) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const speechRecognition = new SpeechRecognition();

      speechRecognition.continuous = true;
      speechRecognition.interimResults = true;
      speechRecognition.lang = language;

      speechRecognition.onstart = () => {
        setIsListening(true);
      };

      speechRecognition.onend = () => {
        setIsListening(false);
      };

      speechRecognition.onresult = (event) => {
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0]?.transcript ?? "";
          }
        }

        if (finalTranscript && textareaRef?.current) {
          onTranscriptionChange?.(
            appendSpoken(textareaRef.current, finalTranscript)
          );
        }
      };

      speechRecognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognitionRef.current = speechRecognition;
      setRecognition(speechRecognition);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [textareaRef, onTranscriptionChange, language]);

  useEffect(() => {
    if (transcribe) {
      return;
    }
    if (isListening) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          setAudioStream(stream);
        })
        .catch((error) => {
          console.error("Error accessing microphone:", error);
        });
    } else {
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      }
    }

    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isListening]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder) {
        recorder.onstop = null;
        recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const toggleListening = useCallback(() => {
    if (!recognition) {
      return;
    }

    if (isListening) {
      recognition.stop();
    } else {
      recognition.start();
    }
  }, [recognition, isListening]);

  const toggleRecording = useCallback(async () => {
    if (!transcribe) {
      return;
    }
    if (recorderRef.current) {
      recorderRef.current.stop();
      return;
    }
    if (startingRef.current) {
      return;
    }
    startingRef.current = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error("Error accessing microphone:", error);
      return;
    } finally {
      startingRef.current = false;
    }
    if (unmountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const recorder = new MediaRecorder(stream);
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => chunks.push(event.data);
    recorder.onstop = async () => {
      stream.getTracks().forEach((track) => track.stop());
      recorderRef.current = null;
      setAudioStream(null);
      setIsListening(false);
      setIsTranscribing(true);
      try {
        const spoken = (
          await transcribe(
            new Blob(chunks, { type: recorder.mimeType }),
            language
          )
        ).trim();
        if (spoken && textareaRef?.current) {
          onTranscriptionChange?.(appendSpoken(textareaRef.current, spoken));
        }
      } finally {
        setIsTranscribing(false);
      }
    };
    recorderRef.current = recorder;
    recorder.start();
    setAudioStream(stream);
    setIsListening(true);
  }, [transcribe, language, textareaRef, onTranscriptionChange]);

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    setPopoverOpen(false);
  };

  if (!transcribe && !isSpeechRecognitionSupported) {
    return null;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PromptInputButton
            aria-label={t("dictate")}
            className={cn(
              "relative overflow-hidden transition-all duration-200",
              isListening && "bg-accent text-accent-foreground",
              className
            )}
            disabled={transcribe ? isTranscribing : !recognition}
            onClick={transcribe ? toggleRecording : toggleListening}
            {...props}
          >
            {isTranscribing ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <MicIcon
                className={cn(
                  "size-4 transition-opacity",
                  isListening && "opacity-0"
                )}
              />
            )}
            <WaveformVisualizer
              isActive={isListening}
              audioStream={audioStream}
            />
          </PromptInputButton>
        </TooltipTrigger>
        {!isListening && (
          <TooltipContent className="z-10000">
            {t(transcribe ? "dictationLanguageModel" : "dictationLanguage", {
              language: languageName,
            })}
          </TooltipContent>
        )}
      </Tooltip>

      {(isHovered || popoverOpen) && !isListening && (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              aria-label={t("dictationLanguagePicker")}
              size="icon"
              variant="ghost"
              className="absolute -right-1 -top-1 size-4 rounded-full bg-background p-0 shadow-sm hover:bg-accent"
              onClick={(e) => {
                e.stopPropagation();
                setPopoverOpen(true);
              }}
            >
              <LanguagesIcon className="size-2.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-56 p-1"
            align="end"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="space-y-1">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Button
                  key={lang.code}
                  variant={language === lang.code ? "default" : "ghost"}
                  className="w-full justify-start"
                  size="sm"
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.label}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export type PromptInputSelectProps = ComponentProps<typeof Select>;

export const PromptInputSelect = (props: PromptInputSelectProps) => (
  <Select {...props} />
);

export type PromptInputSelectTriggerProps = ComponentProps<
  typeof SelectTrigger
>;

export const PromptInputSelectTrigger = ({
  className,
  ...props
}: PromptInputSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      "border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors",
      "hover:bg-accent hover:text-foreground aria-expanded:bg-accent aria-expanded:text-foreground",
      className
    )}
    {...props}
  />
);

export type PromptInputSelectContentProps = ComponentProps<
  typeof SelectContent
>;

export const PromptInputSelectContent = ({
  className,
  ...props
}: PromptInputSelectContentProps) => (
  <SelectContent className={cn(className)} {...props} />
);

export type PromptInputSelectItemProps = ComponentProps<typeof SelectItem>;

export const PromptInputSelectItem = ({
  className,
  ...props
}: PromptInputSelectItemProps) => (
  <SelectItem className={cn(className)} {...props} />
);

export type PromptInputSelectValueProps = ComponentProps<typeof SelectValue>;

export const PromptInputSelectValue = ({
  className,
  ...props
}: PromptInputSelectValueProps) => (
  <SelectValue className={cn(className)} {...props} />
);

export type PromptInputHoverCardProps = ComponentProps<typeof HoverCard>;

export const PromptInputHoverCard = ({
  openDelay = 0,
  closeDelay = 0,
  ...props
}: PromptInputHoverCardProps) => (
  <HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />
);

export type PromptInputHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>;

export const PromptInputHoverCardTrigger = (
  props: PromptInputHoverCardTriggerProps
) => <HoverCardTrigger {...props} />;

export type PromptInputHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>;

export const PromptInputHoverCardContent = ({
  align = "start",
  ...props
}: PromptInputHoverCardContentProps) => (
  <HoverCardContent align={align} {...props} />
);

export type PromptInputTabsListProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabsList = ({
  className,
  ...props
}: PromptInputTabsListProps) => <div className={cn(className)} {...props} />;

export type PromptInputTabProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTab = ({
  className,
  ...props
}: PromptInputTabProps) => <div className={cn(className)} {...props} />;

export type PromptInputTabLabelProps = HTMLAttributes<HTMLHeadingElement>;

export const PromptInputTabLabel = ({
  className,
  ...props
}: PromptInputTabLabelProps) => (
  <h3
    className={cn(
      "mb-2 px-3 font-medium text-muted-foreground text-xs",
      className
    )}
    {...props}
  />
);

export type PromptInputTabBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabBody = ({
  className,
  ...props
}: PromptInputTabBodyProps) => (
  <div className={cn("space-y-1", className)} {...props} />
);

export type PromptInputTabItemProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTabItem = ({
  className,
  ...props
}: PromptInputTabItemProps) => (
  <div
    className={cn(
      "flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent",
      className
    )}
    {...props}
  />
);

export type PromptInputCommandProps = ComponentProps<typeof Command>;

export const PromptInputCommand = ({
  className,
  ...props
}: PromptInputCommandProps) => <Command className={cn(className)} {...props} />;

export type PromptInputCommandInputProps = ComponentProps<typeof CommandInput>;

export const PromptInputCommandInput = ({
  className,
  ...props
}: PromptInputCommandInputProps) => (
  <CommandInput className={cn(className)} {...props} />
);

export type PromptInputCommandListProps = ComponentProps<typeof CommandList>;

export const PromptInputCommandList = ({
  className,
  ...props
}: PromptInputCommandListProps) => (
  <CommandList className={cn(className)} {...props} />
);

export type PromptInputCommandEmptyProps = ComponentProps<typeof CommandEmpty>;

export const PromptInputCommandEmpty = ({
  className,
  ...props
}: PromptInputCommandEmptyProps) => (
  <CommandEmpty className={cn(className)} {...props} />
);

export type PromptInputCommandGroupProps = ComponentProps<typeof CommandGroup>;

export const PromptInputCommandGroup = ({
  className,
  ...props
}: PromptInputCommandGroupProps) => (
  <CommandGroup className={cn(className)} {...props} />
);

export type PromptInputCommandItemProps = ComponentProps<typeof CommandItem>;

export const PromptInputCommandItem = ({
  className,
  ...props
}: PromptInputCommandItemProps) => (
  <CommandItem className={cn(className)} {...props} />
);

export type PromptInputCommandSeparatorProps = ComponentProps<
  typeof CommandSeparator
>;

export const PromptInputCommandSeparator = ({
  className,
  ...props
}: PromptInputCommandSeparatorProps) => (
  <CommandSeparator className={cn(className)} {...props} />
);
